import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';

import {getCurrentScannedNestIds} from 'api/plateRecognitionApi';
import {scanPlate} from 'stores/business/plateRecognitionBusiness';
import {setAllPlatesScanned, setIsPlateEditStateChanged} from 'stores/actions/plateRecognitionAction';
import {selectIsPlateAlreadyTrained, selectIsPlateEditStateChanged} from 'stores/selectors/plateRecognitionSelectors';
import {FastRobotErrorcode} from 'constants/dataEnum';
import NestCard from './NestCard';

import './PlateRecognition.scss';

function PlateRecognition({isEdit}) {
  const dispatch = useDispatch();
  const isPlateAlreadyTrained = useSelector(selectIsPlateAlreadyTrained);
  const isPlateEditStateChanged = useSelector(selectIsPlateEditStateChanged);

  const [nestStates, setNestStates] = useState({
    nest1: {nest: 1, checked: false, scanned: false, isError: false, type: 'plate'},
    nest2: {nest: 2, type: 'caddy'},
    nest3: {nest: 3, checked: false, scanned: false, isError: false, type: 'plate'},
    nest4: {nest: 4, checked: false, scanned: false, isError: false, type: 'plate'},
    nest5: {nest: 5, checked: false, scanned: false, isError: false, type: 'plate'},
    nest6: {nest: 6, checked: false, scanned: false, isError: false, type: 'plate'},
  });

  const totalNests = Object.keys(nestStates).filter((key) => nestStates[key].type === 'plate').length;
  const scannedCount = Object.values(nestStates).filter((n) => n.type === 'plate' && n.scanned && !n.isError).length;

  const toggleCheckbox = (nestKey) => {
    setNestStates((prev) => ({
      ...prev,
      [nestKey]: {...prev[nestKey], checked: !prev[nestKey].checked},
    }));
  };

  const handlePlateScan = async (nestKey) => {
    const nestId = nestStates[nestKey].nest;
    const scanPlateResponse = await dispatch(scanPlate(nestId));

    if (scanPlateResponse === FastRobotErrorcode.LABWARE_NOT_DETECTED) {
      setNestStates((prev) => ({
        ...prev,
        [nestKey]: {...prev[nestKey], scanned: true, isError: true},
      }));
      return;
    }

    if (scanPlateResponse !== FastRobotErrorcode.NoError) {
      handlePlateReScan(nestKey);
      return;
    }

    setNestStates((prev) => ({
      ...prev,
      [nestKey]: {...prev[nestKey], scanned: true, isError: false},
    }));

    if (isEdit && !isPlateEditStateChanged) {
      dispatch(setIsPlateEditStateChanged(true));
    }
  };

  const handlePlateReScan = async (nestKey) => {
    setNestStates((prev) => ({
      ...prev,
      [nestKey]: {...prev[nestKey], checked: false, scanned: false, isError: false},
    }));
    dispatch(setAllPlatesScanned(false));
  };

  const renderNestCard = (nestKey) => {
    const {nest, checked, scanned, isError, type} = nestStates[nestKey];

    if (type === 'caddy') {
      return (
        <div className="nest-card-img">
          <img className="plate-caddy-image" src="./svg/protocol-editor/tip-caddy-rounded.svg" alt="Caddy" />
        </div>
      );
    }

    return (
      <NestCard
        key={nestKey}
        nestNumber={nest}
        checked={checked}
        scanned={scanned}
        isError={isError}
        onCheck={() => toggleCheckbox(nestKey)}
        onScan={() => handlePlateScan(nestKey)}
        onReScan={() => handlePlateReScan(nestKey)}
      />
    );
  };

  const updateNestState = (scannedNestIds) => {
    setNestStates((prev) => {
      const updatedStates = {...prev};
      scannedNestIds.forEach((nestId) => {
        const nestKey = `nest${nestId}`;
        updatedStates[nestKey].scanned = true;
        updatedStates[nestKey].checked = true;
      });
      return updatedStates;
    });
  };

  useEffect(() => {
    if (scannedCount === totalNests) {
      dispatch(setAllPlatesScanned(true));
    }
  }, [scannedCount, totalNests, dispatch]);

  useEffect(() => {
    const fetchScannedNests = async () => {
      try {
        const scannedNestIds = await getCurrentScannedNestIds();
        updateNestState(scannedNestIds);
      } catch (error) {
        console.error('Error fetching scanned nest IDs:', error);
      }
    };

    if (isEdit && isPlateAlreadyTrained) {
      updateNestState([1, 3, 4, 5, 6]);
    } else {
      fetchScannedNests();
    }
  }, [isEdit, isPlateAlreadyTrained, dispatch]);

  return (
    <div className="plate-scan-container">
      <h3 className="plate-scan-header">
        Plate Scan {scannedCount}/{totalNests} complete
      </h3>
      <div className="nests-container">{Object.entries(nestStates).map(([nestKey]) => renderNestCard(nestKey))}</div>
    </div>
  );
}

PlateRecognition.propTypes = {
  isEdit: PropTypes.bool.isRequired,
};

export default PlateRecognition;
